/**
 * Firebase Authentication Context
 *
 * Provides auth state and methods throughout the app.
 * Uses Firebase Auth with email/password sign-in.
 *
 * Dev Mode: Set VITE_DEV_AUTH_BYPASS=true in .env to enable
 * automatic local authentication without Firebase.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { getFirebaseAuth } from '@/services/firebase';

// Dev auth bypass configuration
const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
const DEV_AUTH_EMAIL = import.meta.env.VITE_DEV_AUTH_EMAIL || 'dev@neurograph.io';
const DEV_AUTH_PASSWORD = import.meta.env.VITE_DEV_AUTH_PASSWORD || '';

// Mock user for dev bypass mode
interface MockUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  metadata: Record<string, unknown>;
  providerData: unknown[];
  refreshToken: string;
  tenantId: string | null;
  delete: () => Promise<void>;
  getIdToken: () => Promise<string>;
  getIdTokenResult: () => Promise<unknown>;
  reload: () => Promise<void>;
  toJSON: () => object;
  phoneNumber: string | null;
  photoURL: string | null;
  providerId: string;
}

function createMockUser(email: string): MockUser {
  return {
    uid: 'dev-user-local',
    email,
    displayName: 'Dev User',
    emailVerified: true,
    isAnonymous: false,
    metadata: {},
    providerData: [],
    refreshToken: 'mock-refresh-token',
    tenantId: null,
    delete: async () => {},
    getIdToken: async () => 'mock-id-token',
    getIdTokenResult: async () => ({}),
    reload: async () => {},
    toJSON: () => ({ uid: 'dev-user-local', email }),
    phoneNumber: null,
    photoURL: null,
    providerId: 'password',
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  isDevMode: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const auth = getFirebaseAuth();

    // Dev bypass mode: sign in with fixed dev credentials so we get a real Firebase ID
    // token (required for Firestore rules) without creating throwaway anonymous users.
    // We skip onAuthStateChanged here because it fires null immediately before the
    // sign-in resolves, causing Firestore listeners to start unauthenticated.
    if (DEV_AUTH_BYPASS) {
      console.log(`[Auth] Dev bypass mode enabled - signing in as ${DEV_AUTH_EMAIL}`);
      signInWithEmailAndPassword(auth, DEV_AUTH_EMAIL, DEV_AUTH_PASSWORD)
        .then((credential) => {
          console.log('[Auth] Dev sign-in succeeded');
          setUser(credential.user);
          setLoading(false);
        })
        .catch((err) => {
          console.warn('[Auth] Dev sign-in failed, falling back to mock user', err);
          setUser(createMockUser(DEV_AUTH_EMAIL) as unknown as User);
          setLoading(false);
        });
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);

      if (firebaseUser) {
        console.log(`[Auth] User signed in: ${firebaseUser.email}`);
      } else {
        console.log('[Auth] No user signed in');
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    setLoading(true);

    // Dev bypass mode - instant sign in
    if (DEV_AUTH_BYPASS) {
      console.log('[Auth] Dev bypass - signing in as:', email);
      const mockUser = createMockUser(email);
      setUser(mockUser as unknown as User);
      setLoading(false);
      return;
    }

    try {
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    // Dev bypass mode - just clear the mock user
    if (DEV_AUTH_BYPASS) {
      console.log('[Auth] Dev bypass - signing out');
      setUser(null);
      return;
    }

    try {
      const auth = getFirebaseAuth();
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('[Auth] Sign out error:', err);
    }
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider value={{ user, loading, error, signIn, signOut, clearError, isDevMode: DEV_AUTH_BYPASS }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Map Firebase auth errors to user-friendly messages.
 */
function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;

    const messages: Record<string, string> = {
      'auth/invalid-email': 'Invalid email address',
      'auth/user-disabled': 'This account has been disabled',
      'auth/user-not-found': 'No account found with this email',
      'auth/wrong-password': 'Incorrect password',
      'auth/invalid-credential': 'Invalid email or password',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
    };

    return messages[code] || `Authentication error: ${code}`;
  }

  return 'An unexpected error occurred';
}
